import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { dms, inr, niceDate } from "./api";

const dateOnly = (d) => {
  if (!d) return "-";
  const s = String(d);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return niceDate(d);
  return new Date(s + "T00:00:00Z").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit", timeZone: "UTC" });
};

// Date-only strings (YYYY-MM-DD) must not go through timezone conversion,
// otherwise they show as "05:30 am" in IST. Anything else keeps niceDate().
import { Button } from "@/components/ui/button";
import { Printer, X, Package } from "lucide-react";

function PrintFrame({ children, title }) {
  return (
    <div className="min-h-screen bg-slate-100 p-2 sm:p-6 print:p-0 print:bg-white">
      <style>{`
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
        @media screen and (max-width: 639px) {
          .pf-items { min-width: 720px; }
          .pf-body .grid-cols-4 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .pf-body .grid-cols-2 { grid-template-columns: 1fr !important; gap: 1rem !important; }
          .po-clip { clip-path: none !important; }
        }
      `}</style>
      <div className="max-w-3xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4 print:hidden">
          <div className="font-semibold">{title}</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => window.close()}><X size={14} className="mr-1" /> Close</Button>
            <Button onClick={() => window.print()} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" size="sm" data-testid="print-btn"><Printer size={14} className="mr-1" /> Print / Save PDF</Button>
          </div>
        </div>
        <div className="pf-body bg-white shadow-lg rounded-lg print:shadow-none print:rounded-none p-3 sm:p-8 print:p-6 overflow-x-auto print:overflow-visible">{children}</div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between text-sm py-1"><span className="text-slate-500">{label}</span><span className="text-slate-900 font-medium text-right">{value}</span></div>;
}

// ─────────────────────────────────────────────────────────────────────────
// Vyapar-style Tax Invoice (shared by Primary e-Bill, Retailer bill, Direct sale)
// Renders from backend `invoice` object.
// ─────────────────────────────────────────────────────────────────────────
function BillToDetails({ party }) {
  if (!party) return null;
  return (
    <div className="text-xs">
      <div className="font-bold text-sm text-slate-900">{party.name || "—"}</div>
      {party.address && <div className="whitespace-pre-wrap text-slate-700 mt-0.5">{party.address}</div>}
      <table className="mt-1"><tbody>
        {party.phone && <tr><td className="text-slate-500 pr-2 align-top">Contact No.:</td><td className="font-medium">{party.phone}</td></tr>}
        {party.gstin && <tr><td className="text-slate-500 pr-2 align-top">GSTIN Number:</td><td className="font-mono font-medium">{party.gstin}</td></tr>}
        {(party.state || party.state_code) && <tr><td className="text-slate-500 pr-2 align-top">State:</td><td className="font-medium">{party.state}{party.state_code ? ` (${party.state_code})` : ""}</td></tr>}
      </tbody></table>
    </div>
  );
}

function ShipToDetails({ party }) {
  if (!party) return null;
  return (
    <div className="text-xs">
      <div className="font-bold text-sm text-slate-900">{party.name || "—"}</div>
      {party.address && <div className="whitespace-pre-wrap text-slate-700 mt-0.5">{party.address}</div>}
    </div>
  );
}

function TdV2Table({ rows, brand }) {
  const r2 = (x) => Math.round(x * 100) / 100;
  const sum = (f) => rows.reduce((s, r) => s + (Number(f(r)) || 0), 0);
  const rate = (u) => (Math.abs(u * 100 - Math.round(u * 100)) < 1e-7 ? u.toFixed(2) : u.toFixed(4));
  const litres = r2(sum((r) => (r.unit === "L" ? r.qty : 0)));
  const kgs = r2(sum((r) => (r.unit === "kg" ? r.qty : 0)));
  const qtyTotal = [litres ? litres + " L" : "", kgs ? kgs + " kg" : ""].filter(Boolean).join(" + ");
  const th = "p-2 border border-black/10";
  const cell = "p-2 border border-slate-200";
  return (
    <table className="pf-items w-full text-xs border border-slate-300" data-testid="td-v2-table">
      <thead>
        <tr className="text-white text-left" style={{ background: brand }}>
          <th className={th}>Product</th>
          <th className={th + " text-right"}>MRP</th>
          <th className={th + " text-right"}>Qty (Boxes)</th>
          <th className={th + " text-right"}>Qty (Pieces)</th>
          <th className={th + " text-right"}>Qty in Ltr/Kg</th>
          <th className={th + " text-right"}>DLP excl. GST</th>
          <th className={th + " text-right"}>Total TD incl. GST</th>
          <th className={th + " text-right"}>GST after TD</th>
          <th className={th + " text-right"}>Amount incl. GST</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-slate-200">
            <td className={cell}>
              <div className="font-medium">{r.name}</div>
              {r.sku ? <div className="text-[10px] font-mono text-slate-500">{r.sku}</div> : null}
            </td>
            <td className={cell + " text-right"}>{r.mrp != null && r.mrp !== "" ? inr(r.mrp) : "\u2014"}</td>
            <td className={cell + " text-right"}>{r.boxes != null ? r.boxes : "\u2014"}</td>
            <td className={cell + " text-right"}>{r.pieces || "\u2014"}</td>
            <td className={cell + " text-right"}>{r.qty ? r2(r.qty) + " " + r.unit : "\u2014"}</td>
            <td className={cell + " text-right"}>{r.dlp != null ? "\u20b9" + rate(r.dlp) + " \u00d7 " + r.pieces : "\u2014"}</td>
            <td className={cell + " text-right"}>{inr(r.td || 0)}</td>
            <td className={cell + " text-right"}>{inr(r.gst)} <span className="text-slate-400">({r.gstPct}%)</span></td>
            <td className={cell + " text-right font-semibold"}>{inr(r.amount)}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={9} className="p-4 text-center text-slate-400 border border-slate-200">No items</td></tr>}
      </tbody>
      <tfoot>
        <tr className="text-white font-bold" style={{ background: brand }}>
          <td className={th}>Total</td>
          <td className={th}></td>
          <td className={th + " text-right"}>{r2(sum((r) => r.boxes))}</td>
          <td className={th + " text-right"}>{r2(sum((r) => r.pieces))}</td>
          <td className={th + " text-right"}>{qtyTotal}</td>
          <td className={th}></td>
          <td className={th + " text-right"}>{inr(r2(sum((r) => r.td)))}</td>
          <td className={th + " text-right"}>{inr(r2(sum((r) => r.gst)))}</td>
          <td className={th + " text-right"}>{inr(r2(sum((r) => r.amount)))}</td>
        </tr>
      </tfoot>
    </table>
  );
}

function InvoiceCopy({ inv, copyLabel }) {
  const t = inv.totals || {};
  const seller = inv.seller || {};
  const transport = inv.transport || {};
  const hasTransport = transport && (transport.mode || transport.vehicle_no || transport.transporter || transport.lr_no);
  const items = inv.items || [];
  const totalQtyBoxes = Math.round(items.reduce((s, it) => s + (it.qty_boxes || 0), 0) * 100) / 100;
  const totalQtyPcs = Math.round(items.reduce((s, it) => s + (it.qty_boxes ? 0 : (it.qty_pcs || 0)), 0) * 100) / 100;
  const totalQtyLabel = [totalQtyBoxes ? totalQtyBoxes + " Box" : "", totalQtyPcs ? totalQtyPcs + " Pcs" : ""].filter(Boolean).join(" + ") || "0";
  const totalPieces = Math.round(items.reduce((s, it) => s + (it.total_pieces || 0), 0) * 100) / 100;
  const totalLiters = Math.round(items.reduce((s, it) => s + (it.total_liters || 0), 0) * 100) / 100;
  const totalGst = items.reduce((s, it) => s + (it.gst_amt || 0), 0);
  const totalAmount = items.reduce((s, it) => s + (it.amount || 0), 0);
  const v2 = t.calc_version === 2;
  const BRAND = "#a67c00";

  return (
    <div className="text-slate-900 text-sm" data-testid="vyapar-invoice">
      {/* Header banner */}
      <div className="flex items-center justify-between px-1 pb-1">
        <div />
        <div className="text-xl font-extrabold" style={{ color: BRAND }}>{inv.doc_title || "Tax Invoice"}</div>
      </div>
      <div className="relative mb-1">
        <div className="po-clip flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-white px-4 py-3" style={{ background: BRAND, clipPath: "polygon(0 0, 100% 0, 100% 100%, 8% 100%, 0 60%)" }}>
          <div className="flex items-center gap-2">
            {seller.logo_url ? (
              <img src={seller.logo_url} alt="logo" className="h-10 w-10 object-contain bg-black rounded" />
            ) : (
              <div className="h-10 w-10 rounded bg-black flex items-center justify-center text-white font-bold text-xs">GO</div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[11px]">
            {seller.phone && <span>{seller.phone}</span>}
            {seller.email && <span>{seller.email}</span>}
          </div>
          <div className="text-[11px] sm:text-right whitespace-pre-wrap sm:max-w-[220px]">{seller.address}</div>
        </div>
        <div className="po-clip text-white px-4 py-2 -mt-1 relative z-10" style={{ background: "#1e293b", clipPath: "polygon(0 0, 62% 0, 52% 100%, 0 100%)" }}>
          <div className="text-lg font-extrabold uppercase tracking-tight">{seller.name}</div>
          {seller.gstin && <div className="text-[10px] mt-0.5">GSTIN: <span className="font-mono">{seller.gstin}</span></div>}
          {(seller.state || seller.state_code) && <div className="text-[10px]">State: {seller.state}{seller.state_code ? ` (${seller.state_code})` : ""}</div>}
        </div>
      </div>

      {/* Bill To (left) / Invoice meta + Ship To (right) */}
      <div className="grid grid-cols-2 gap-8 mb-4 px-1">
        <div>
          <div className="text-xs font-bold uppercase mb-1" style={{ color: BRAND }}>Bill To</div>
          <BillToDetails party={inv.bill_to} />
        </div>
        <div>
          <table className="text-xs w-full mb-3">
            <tbody>
              <tr><td className="text-slate-500 pr-2 py-0.5 align-top w-36">Invoice No.:</td><td className="font-semibold">{inv.doc_no}</td></tr>
              <tr><td className="text-slate-500 pr-2 py-0.5 align-top">Date:</td><td className="font-semibold">{dateOnly(inv.date)}</td></tr>
              <tr><td className="text-slate-500 pr-2 py-0.5 align-top">Place of Supply:</td><td className="font-semibold">{(inv.bill_to && (inv.bill_to.state || inv.bill_to.state_code)) || "—"}</td></tr>
              {items[0]?.hsn && <tr><td className="text-slate-500 pr-2 py-0.5 align-top">HSN/SAC:</td><td className="font-semibold font-mono">{items[0].hsn}</td></tr>}
              {inv.eway_bill_no && <tr><td className="text-slate-500 pr-2 py-0.5 align-top">E-way Bill number:</td><td className="font-semibold">{inv.eway_bill_no}</td></tr>}
            </tbody>
          </table>
          <div className="text-xs font-bold uppercase mb-1" style={{ color: BRAND }}>Ship To :</div>
          <ShipToDetails party={inv.ship_to} />
        </div>
      </div>

      {hasTransport && (
        <div className="grid grid-cols-4 gap-4 text-xs mb-4 border-t border-b border-slate-200 py-2 px-1">
          <div><span className="text-slate-500">Transport: </span>{transport.mode || "—"}</div>
          <div><span className="text-slate-500">Vehicle: </span>{transport.vehicle_no || "—"}</div>
          <div><span className="text-slate-500">Transporter: </span>{transport.transporter || "—"}</div>
          <div><span className="text-slate-500">LR No: </span>{transport.lr_no || "—"}</div>
        </div>
      )}

      {/* Items table */}
      {v2 && <TdV2Table brand={BRAND} rows={items.map((it) => ({ name: it.name, sku: "", mrp: it.mrp, boxes: it.qty_boxes, pieces: it.total_pieces, qty: it.total_liters, unit: it.qty_unit, dlp: it.dlp_ex_gst, td: it.td_incl, gst: it.gst_amt, gstPct: it.gst_pct, amount: it.amount }))} />}
      <table style={v2 ? { display: "none" } : undefined} className="pf-items w-full text-xs border border-slate-300">
        <thead>
          <tr className="text-white text-left" style={{ background: BRAND }}>
            <th className="p-2 border border-black/10">Product</th>
            <th className="p-2 border border-black/10 text-right">MRP</th>
            <th className="p-2 border border-black/10 text-right">Quantity</th>
            <th className="p-2 border border-black/10">Unit</th>
            <th className="p-2 border border-black/10 text-right w-12">Quantity in piece</th>
            <th className="p-2 border border-black/10 text-right w-20">No. of Litres</th>
            <th className="p-2 border border-black/10 text-right">DLP</th>
            <th className="p-2 border border-black/10 text-right">GST</th>
            <th className="p-2 border border-black/10 text-right w-10">Trade Discount</th>
            <th className="p-2 border border-black/10 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i} className="border-t border-slate-200">
              <td className="p-2 border border-slate-200">
                <div className="font-medium flex items-center gap-1">
                  {it.name}
                  {it.pcs_per_box > 0 && (
                    <span className="text-[9px] font-bold bg-slate-100 text-slate-600 px-1 py-0.5 rounded flex items-center gap-0.5 shrink-0">
                      <Package size={9} /> {it.pcs_per_box}
                    </span>
                  )}
                </div>
              </td>
              <td className="p-2 border border-slate-200 text-right">{it.mrp != null ? inr(it.mrp) : "—"}</td>
              <td className="p-2 border border-slate-200 text-right">{it.qty_boxes || it.qty_pcs || "—"}</td>
              <td className="p-2 border border-slate-200">{it.unit || "Box"}</td>
              <td className="p-2 border border-slate-200 text-right w-12">{it.total_pieces || "—"}</td>
              <td className="p-2 border border-slate-200 text-right w-20">{it.total_liters ? it.total_liters.toFixed(2) : "—"}</td>
              <td className="p-2 border border-slate-200 text-right">
                {it.total_pieces > 0
                  ? <>{inr(Math.round((it.taxable / it.total_pieces) * 100) / 100)} × {it.total_pieces}</>
                  : inr(it.rate)}
              </td>
              <td className="p-2 border border-slate-200 text-right">{inr(it.gst_amt)} <span className="text-slate-400">({it.gst_pct}%)</span></td>
              <td className="p-2 border border-slate-200 text-right w-10">{it.trade_discount || "—"}</td>
              <td className="p-2 border border-slate-200 text-right font-semibold">{inr(it.amount)}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={10} className="p-4 text-center text-slate-400 border border-slate-200">No items</td></tr>}
        </tbody>
        <tfoot>
          <tr className="text-white font-bold" style={{ background: BRAND }}>
            <td className="p-2 border border-black/10">Total</td>
            <td className="p-2 border border-black/10"></td>
            <td className="p-2 border border-black/10 text-right">{totalQtyLabel}</td>
            <td className="p-2 border border-black/10"></td>
            <td className="p-2 border border-black/10 text-right w-12">{totalPieces}</td>
            <td className="p-2 border border-black/10 text-right w-20">{totalLiters ? totalLiters.toFixed(2) : ""}</td>
            <td className="p-2 border border-black/10"></td>
            <td className="p-2 border border-black/10 text-right">{inr(totalGst)}</td>
            <td className="p-2 border border-black/10 w-10"></td>
            <td className="p-2 border border-black/10 text-right">{inr(t.grand_total != null ? t.grand_total : totalAmount)}</td>
          </tr>
        </tfoot>
      </table>

      {/* Pay To + Amount in words + Terms (left) / Totals box (right) */}
      <div className="grid grid-cols-2 gap-8 mt-4 px-1">
        <div>
          <div className="text-xs font-bold uppercase mb-1" style={{ color: BRAND }}>Pay To:</div>
          <div className="text-xs space-y-0.5">
            {seller.bank_name && <div>Bank Name: <span className="font-medium">{seller.bank_name}</span></div>}
            {seller.bank_account && <div>Bank Account No.: <span className="font-mono">{seller.bank_account}</span></div>}
            {seller.bank_ifsc && <div>Bank IFSC code: <span className="font-mono">{seller.bank_ifsc}</span></div>}
            {(seller.bank_name || seller.bank_account) && <div>Account Holder&apos;s Name: <span className="font-medium">{seller.name}</span></div>}
          </div>

          <div className="text-xs font-bold uppercase mt-4 mb-1" style={{ color: BRAND }}>Invoice Amount In Words</div>
          <div className="text-xs font-semibold">{inv.amount_in_words}</div>

          {inv.terms && (
            <>
              <div className="text-xs font-bold uppercase mt-4 mb-1" style={{ color: BRAND }}>Terms &amp; Conditions</div>
              <div className="text-xs text-slate-700 whitespace-pre-wrap">{inv.terms}</div>
            </>
          )}

          <div className="mt-10 text-xs">
            <div>For: {seller.name}</div>
            <div className="mt-10 border-t border-slate-400 pt-1 w-40 text-center">{inv.signatory || "Authorized Signatory"}</div>
          </div>
        </div>

        <div className="text-xs border border-slate-300 self-start">
          {v2 && <Row label="Gross amount incl. GST" value={inr(t.gross_incl_before_td)} />}
          {v2 && <Row label="Less: Total TD incl. GST" value={"- " + inr(t.trade_discount_total)} />}
          <Row label={v2 ? "Net taxable (after TD)" : "Sub Total"} value={inr(t.subtotal)} />
          {t.is_interstate ? (
            <Row label="IGST" value={inr(t.igst)} />
          ) : (
            <>
              <Row label="SGST" value={inr(t.sgst)} />
              <Row label="CGST" value={inr(t.cgst)} />
            </>
          )}
          {!v2 && (t.trade_discount_total || 0) > 0 && <Row label="Trade Discount" value={"- " + inr(t.trade_discount_total)} />}
          {Math.abs(t.round_off || 0) > 0.001 && <Row label="Round off" value={inr(t.round_off)} />}
          <div className="text-white font-bold flex justify-between px-2 py-1.5" style={{ background: BRAND }}>
            <span>Total</span><span>{inr(t.grand_total)}</span>
          </div>
          {t.received_amount != null && (
            <>
              <Row label="Received" value={inr(t.received_amount)} />
              <Row label="Balance" value={inr(t.balance_due != null ? t.balance_due : (t.grand_total - t.received_amount))} />
            </>
          )}
        </div>
      </div>

      {inv.message && (
        <div className="mt-4 p-3 rounded-lg bg-[#faf6e6] border border-[#c9a227]/30 text-sm text-slate-700">{inv.message}</div>
      )}

      {inv.acknowledgement_enabled && (
        <div className="mt-6 border-2 border-dashed border-slate-400 p-3" data-testid="invoice-acknowledgement">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Acknowledgement</div>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <div>{seller.name}</div>
              <div className="text-slate-500 mt-1">Invoice No: <span className="font-semibold text-slate-800">{inv.doc_no}</span></div>
              <div className="text-slate-500">Date: <span className="font-semibold text-slate-800">{dateOnly(inv.date)}</span></div>
              <div className="text-slate-500">Invoice Amount: <span className="font-semibold text-slate-800">{inr(t.grand_total)}</span></div>
            </div>
            <div className="flex flex-col justify-end">
              <div className="mt-10 border-t border-slate-400 pt-1 text-center">Receiver&apos;s Seal &amp; Sign</div>
            </div>
          </div>
        </div>
      )}

      {/* Footer decorative bar */}
      <div className="relative mt-8 h-6">
        <div className="absolute inset-0" style={{ background: BRAND, clipPath: "polygon(0 0, 60% 0, 68% 100%, 0 100%)" }} />
        <div className="absolute inset-0" style={{ background: "#1e293b", clipPath: "polygon(65% 0, 100% 0, 100% 100%, 73% 100%)" }} />
      </div>
    </div>
  );
}

export function VyaparInvoice({ inv }) {
  if (!inv) return null;
  return (
    <>
      <InvoiceCopy inv={inv} copyLabel="ORIGINAL FOR RECIPIENT" />
      <div className="my-8 border-t-2 border-dashed border-slate-300 print:hidden" />
      <div className="print:break-before-page" />
      <InvoiceCopy inv={inv} copyLabel="DUPLICATE FOR TRANSPORTER" />
    </>
  );
}

export function PrintEbillPage() {
  const { id } = useParams();
  const [eb, setEb] = useState(null);
  useEffect(() => { dms.printEbill(id).then(setEb); }, [id]);
  if (!eb) return <div className="p-8 text-center text-slate-500">Loading…</div>;
  return (
    <PrintFrame title={`e-Bill ${eb.ebill_no}`}>
      <VyaparInvoice inv={eb.invoice} />
    </PrintFrame>
  );
}

export function PrintRetailerBillPage() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  useEffect(() => { dms.printRetailerBill(id).then(setB); }, [id]);
  if (!b) return <div className="p-8 text-center text-slate-500">Loading…</div>;
  return (
    <PrintFrame title={`Bill ${b.bill_no}`}>
      <VyaparInvoice inv={b.invoice} />
    </PrintFrame>
  );
}

export function PrintChallanPage() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  useEffect(() => { dms.printChallan(id).then(setC).catch(() => setC(false)); }, [id]);
  if (c === false) return <div className="p-8 text-center text-rose-500">Challan not found</div>;
  if (!c) return <div className="p-8 text-center text-slate-500">Loading…</div>;
  const items = c.items || [];
  return (
    <PrintFrame title={`Delivery Challan ${c.challan_no}`}>
      <div className="border-b-2 border-[#a67c00] pb-4 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-2xl font-bold text-slate-900">DELIVERY CHALLAN</div>
            <div className="text-sm text-slate-500">Not a Tax Invoice</div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-[#a67c00]">{c.distributor?.name || c.company_name}</div>
            <div className="text-xs text-slate-500">{c.distributor?.address}</div>
            {c.distributor?.kyc?.gstin && <div className="text-xs text-slate-500">GSTIN: {c.distributor.kyc.gstin}</div>}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-8 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Deliver To</div>
          <div className="font-semibold text-slate-900">{c.retailer?.name}</div>
          <div className="text-xs text-slate-600">{c.retailer?.address}</div>
          <div className="text-xs text-slate-600">Ph: {c.retailer?.phone}</div>
        </div>
        <div>
          <Row label="Challan #" value={c.challan_no} />
          <Row label="Date" value={niceDate(c.created_at)} />
          <Row label="Order Ref" value={c.order_no} />
          {c.invoice_no && <Row label="Invoice #" value={c.invoice_no} />}
        </div>
      </div>
      <table className="w-full text-sm border border-slate-200 mb-6">
        <thead><tr className="bg-slate-50 text-left"><th className="p-2">#</th><th className="p-2">Product</th><th className="p-2 text-right">Boxes</th><th className="p-2 text-right">Pcs</th></tr></thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="p-2">{i + 1}</td>
              <td className="p-2"><div className="font-medium">{it.product_name}</div><div className="text-xs font-mono text-slate-500">{it.sku_code}</div></td>
              <td className="p-2 text-right">{it.qty_boxes || 0}</td>
              <td className="p-2 text-right">{it.qty_pcs || 0}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-slate-400">No items</td></tr>}
        </tbody>
      </table>
      {items.length > 0 && (() => {
        const totalBoxes = items.reduce((s, it) => s + (it.qty_boxes || 0), 0);
        const totalPcs = items.reduce((s, it) => s + (it.qty_pcs || 0), 0);
        return (
          <div className="flex justify-end mb-6">
            <div className="w-56 text-xs text-slate-600 border-t border-slate-200 pt-2">
              <Row label="Total Boxes" value={totalBoxes} />
              {totalPcs > 0 && <Row label="Total Pcs" value={totalPcs} />}
            </div>
          </div>
        );
      })()}
      <div className="grid grid-cols-2 gap-8 mt-12 text-xs text-slate-500">
        <div className="border-t border-slate-300 pt-2 text-center">Received By</div>
        <div className="border-t border-slate-300 pt-2 text-center">Authorised Signatory</div>
      </div>
    </PrintFrame>
  );
}



export function PrintPurchaseOrderPage() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  useEffect(() => { dms.printPurchaseOrder(id).then(setO); }, [id]);
  if (!o) return <div className="p-8 text-center text-slate-500">Loading…</div>;
  const BRAND = "#a67c00";
  const items = o.items || [];
  const totalQty = Math.round(items.reduce((s, it) => s + (it.qty_boxes_ordered || 0), 0) * 100) / 100;
  const totalPieces = Math.round(items.reduce((s, it) => s + (it.box_qty || 0) * (it.qty_boxes_ordered || 0), 0) * 100) / 100;
  const totalLiters = Math.round(items.reduce((s, it) => s + (it.liter_per_box || 0) * (it.qty_boxes_ordered || 0), 0) * 100) / 100;
  const halfGst = Math.round((o.gst_total / 2) * 100) / 100;
  const v2 = o.td_calc_version === 2;
  const gstHalfLabel = ((items[0] && items[0].gst_pct) || 18) / 2;
  const dist = o.distributor || {};
  return (
    <PrintFrame title={`Purchase Order ${o.order_no}`}>
      <div className="text-slate-900 text-sm" data-testid="po-vyapar">
        {/* Header banner */}
        <div className="flex items-center justify-between px-1 pb-1">
          <div />
          <div className="text-xl font-extrabold" style={{ color: BRAND }}>Purchase Order</div>
        </div>
        <div className="relative mb-1">
          <div className="po-clip flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-white px-4 py-3" style={{ background: BRAND, clipPath: "polygon(0 0, 100% 0, 100% 100%, 8% 100%, 0 60%)" }}>
            <div className="flex items-center gap-2">
              {o.company_logo_url ? (
                <img src={o.company_logo_url} alt="logo" className="h-10 w-10 object-contain bg-black rounded" />
              ) : (
                <div className="h-10 w-10 rounded bg-black flex items-center justify-center text-white font-bold text-xs">GO</div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[11px]">
              {o.company_phone && <span>{o.company_phone}</span>}
              {o.company_email && <span>{o.company_email}</span>}
            </div>
            <div className="text-[11px] sm:text-right whitespace-pre-wrap sm:max-w-[220px]">{o.company_address}</div>
          </div>
          <div className="po-clip text-white px-4 py-2 -mt-1 relative z-10" style={{ background: "#1e293b", clipPath: "polygon(0 0, 62% 0, 52% 100%, 0 100%)" }}>
            <div className="text-lg font-extrabold uppercase tracking-tight">{o.company_name}</div>
            {o.company_gstin && <div className="text-[10px] mt-0.5">GSTIN: <span className="font-mono">{o.company_gstin}</span></div>}
            {(o.company_state || o.company_state_code) && <div className="text-[10px]">State: {o.company_state}{o.company_state_code ? ` (${o.company_state_code})` : ""}</div>}
          </div>
        </div>

        {/* Buyer / PO meta */}
        <div className="grid grid-cols-2 gap-8 mb-4 px-1">
          <div>
            <div className="text-xs font-bold uppercase mb-1" style={{ color: BRAND }}>Buyer (Distributor)</div>
            <div className="text-xs">
              <div className="font-bold text-sm text-slate-900">{dist.name || "—"}</div>
              {dist.address && <div className="whitespace-pre-wrap text-slate-700 mt-0.5">{dist.address}</div>}
              {dist.phone && <div className="mt-1"><span className="text-slate-500">Ph: </span>{dist.phone}</div>}
            </div>
          </div>
          <div>
            <table className="text-xs w-full mb-3">
              <tbody>
                <tr><td className="text-slate-500 pr-2 py-0.5 align-top w-36">PO #:</td><td className="font-semibold">{o.order_no}</td></tr>
                <tr><td className="text-slate-500 pr-2 py-0.5 align-top">Date:</td><td className="font-semibold">{niceDate(o.created_at)}</td></tr>
                <tr><td className="text-slate-500 pr-2 py-0.5 align-top">Status:</td><td className="font-semibold">{o.status?.replace(/_/g, " ")}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Items table */}
        {v2 && <TdV2Table brand={BRAND} rows={items.map((it) => ({ name: it.product_name, sku: it.sku_code, mrp: it.mrp, boxes: it.qty_boxes_ordered, pieces: it.pieces, qty: it.eligible_qty, unit: it.qty_unit, dlp: it.unit_dlp_ex_gst, td: it.td_total_incl, gst: it.line_gst, gstPct: it.gst_pct, amount: it.line_total }))} />}
        <table style={v2 ? { display: "none" } : undefined} className="pf-items w-full text-xs border border-slate-300">
          <thead>
            <tr className="text-white text-left" style={{ background: BRAND }}>
              <th className="p-2 border border-black/10">Product</th>
              <th className="p-2 border border-black/10 text-right">Quantity</th>
              <th className="p-2 border border-black/10">Unit</th>
              <th className="p-2 border border-black/10 text-right">No. of Pieces</th>
              <th className="p-2 border border-black/10 text-right">No. of Liters</th>
              <th className="p-2 border border-black/10 text-right">Trade Discount</th>
              <th className="p-2 border border-black/10 text-right">Rate</th>
              <th className="p-2 border border-black/10 text-right">GST</th>
              <th className="p-2 border border-black/10 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const pieces = (it.box_qty || 0) * (it.qty_boxes_ordered || 0);
              const liters = (it.liter_per_box || 0) * (it.qty_boxes_ordered || 0);
              return (
                <tr key={i} className="border-t border-slate-200">
                  <td className="p-2 border border-slate-200">
                    <div className="font-medium">{it.product_name}</div>
                    <div className="text-[10px] font-mono text-slate-500">{it.sku_code}</div>
                  </td>
                  <td className="p-2 border border-slate-200 text-right">{it.qty_boxes_ordered}</td>
                  <td className="p-2 border border-slate-200">{Number.isInteger(it.qty_boxes_ordered) ? "Box" : "-"}</td>
                  <td className="p-2 border border-slate-200 text-right">{pieces || "—"}</td>
                  <td className="p-2 border border-slate-200 text-right">{liters ? liters.toFixed(2) : "—"}</td>
                  <td className="p-2 border border-slate-200 text-right">{it.trade_discount || "—"}</td>
                  <td className="p-2 border border-slate-200 text-right">
                    {it.margin_pct > 0 ? (
                      <>
                        <div className="line-through text-slate-400 text-[10px]">{inr(it.list_price)}</div>
                        <div>{inr(it.display_price != null ? it.display_price : (it.list_price - (it.list_price * it.margin_pct / 100)))}</div>
                        <div className="text-[10px] text-emerald-600">-{it.margin_pct}%</div>
                      </>
                    ) : inr(it.unit_price)}
                  </td>
                  <td className="p-2 border border-slate-200 text-right">{inr(it.line_gst)}</td>
                  <td className="p-2 border border-slate-200 text-right font-semibold">{inr(it.line_total)}</td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={9} className="p-4 text-center text-slate-400 border border-slate-200">No items</td></tr>}
          </tbody>
          <tfoot>
            <tr className="text-white font-bold" style={{ background: BRAND }}>
              <td className="p-2 border border-black/10">Total</td>
              <td className="p-2 border border-black/10 text-right">{totalQty}</td>
              <td className="p-2 border border-black/10"></td>
              <td className="p-2 border border-black/10 text-right">{totalPieces || ""}</td>
              <td className="p-2 border border-black/10 text-right">{totalLiters ? totalLiters.toFixed(2) : ""}</td>
              <td className="p-2 border border-black/10"></td>
              <td className="p-2 border border-black/10"></td>
              <td className="p-2 border border-black/10 text-right">{inr(o.gst_total)}</td>
              <td className="p-2 border border-black/10 text-right">{inr(o.total)}</td>
            </tr>
          </tfoot>
        </table>

        {/* Pay To + Amount in words + Terms (left) / Totals box (right) */}
        <div className="grid grid-cols-2 gap-8 mt-4 px-1">
          <div>
            {(o.company_bank_name || o.company_bank_account) && (
              <>
                <div className="text-xs font-bold uppercase mb-1" style={{ color: BRAND }}>Pay To:</div>
                <div className="text-xs space-y-0.5">
                  {o.company_bank_name && <div>Bank Name: <span className="font-medium">{o.company_bank_name}</span></div>}
                  {o.company_bank_account && <div>Bank Account No.: <span className="font-mono">{o.company_bank_account}</span></div>}
                  {o.company_bank_ifsc && <div>Bank IFSC code: <span className="font-mono">{o.company_bank_ifsc}</span></div>}
                  <div>Account Holder&apos;s Name: <span className="font-medium">{o.company_name}</span></div>
                </div>
              </>
            )}

            {o.amount_in_words && (
              <>
                <div className="text-xs font-bold uppercase mt-4 mb-1" style={{ color: BRAND }}>Amount In Words</div>
                <div className="text-xs font-semibold">{o.amount_in_words}</div>
              </>
            )}

            {o.invoice_terms && (
              <>
                <div className="text-xs font-bold uppercase mt-4 mb-1" style={{ color: BRAND }}>Terms &amp; Conditions</div>
                <div className="text-xs text-slate-700 whitespace-pre-wrap">{o.invoice_terms}</div>
              </>
            )}

            <div className="mt-10 text-xs">
              <div>For: {o.company_name}</div>
              <div className="mt-10 border-t border-slate-400 pt-1 w-40 text-center">{o.invoice_signatory || "Authorized Signatory"}</div>
            </div>
          </div>

          <div className="text-xs border border-slate-300 self-start">
            {v2 && <Row label="Gross amount incl. GST" value={inr(o.gross_incl_before_td)} />}
            {v2 && <Row label="Less: Total TD incl. GST" value={"- " + inr(o.trade_discount_total)} />}
            <Row label={v2 ? "Net taxable (after TD)" : "Subtotal"} value={inr(o.subtotal)} />
            {o.tax_error ? (
              <Row label="GST (type pending)" value={o.tax_error} />
            ) : o.tax_split && o.tax_split.tax_type === "IGST" ? (
              <Row label={`IGST (${gstHalfLabel * 2}%)`} value={inr(o.tax_split.igst)} />
            ) : (
              <>
                <Row label={v2 ? `SGST (${gstHalfLabel}%)` : "SGST (9%)"} value={inr(o.tax_split ? o.tax_split.sgst : halfGst)} />
                <Row label={v2 ? `CGST (${gstHalfLabel}%)` : "CGST (9%)"} value={inr(o.tax_split ? o.tax_split.cgst : Math.round((o.gst_total - halfGst) * 100) / 100)} />
              </>
            )}
            {!v2 && (o.trade_discount_total || 0) > 0 && <Row label="Trade Discount" value={"- " + inr(o.trade_discount_total)} />}
            <div className="text-white font-bold flex justify-between px-2 py-1.5" style={{ background: BRAND }}>
              <span>Grand Total</span><span>{inr(o.total)}</span>
            </div>
          </div>
        </div>

        {o.invoice_message && (
          <div className="mt-4 p-3 rounded-lg bg-[#faf6e6] border border-[#c9a227]/30 text-sm text-slate-700">{o.invoice_message}</div>
        )}

        <div className="mt-6 border-2 border-dashed border-slate-400 p-3" data-testid="po-acknowledgement">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Acknowledgement</div>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <div>{o.company_name}</div>
              <div className="text-slate-500 mt-1">PO No: <span className="font-semibold text-slate-800">{o.order_no}</span></div>
              <div className="text-slate-500">Date: <span className="font-semibold text-slate-800">{niceDate(o.created_at)}</span></div>
              <div className="text-slate-500">Amount: <span className="font-semibold text-slate-800">{inr(o.total)}</span></div>
            </div>
            <div className="flex flex-col justify-end">
              <div className="mt-10 border-t border-slate-400 pt-1 text-center">Receiver&apos;s Seal &amp; Sign</div>
            </div>
          </div>
        </div>

        {/* Footer decorative bar */}
        <div className="relative mt-8 h-6">
          <div className="absolute inset-0" style={{ background: BRAND, clipPath: "polygon(0 0, 60% 0, 68% 100%, 0 100%)" }} />
          <div className="absolute inset-0" style={{ background: "#1e293b", clipPath: "polygon(65% 0, 100% 0, 100% 100%, 73% 100%)" }} />
        </div>
      </div>
    </PrintFrame>
  );
}

export function PrintDocumentPage() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { dms.printDocument(id).then(setD); }, [id]);
  if (!d) return <div className="p-8 text-center text-slate-500">Loading…</div>;

  const DOC_STYLES = {
    estimate:         { color: "#3b82f6", bg: "bg-blue-50",    border: "border-blue-500",    text: "text-blue-700",    tag: "PROPOSAL" },
    delivery_challan: { color: "#059669", bg: "bg-emerald-50", border: "border-emerald-600", text: "text-emerald-700", tag: "DISPATCH" },
    sale_return:      { color: "#e11d48", bg: "bg-rose-50",    border: "border-rose-600",    text: "text-rose-700",    tag: "RETURN" },
    credit_note:      { color: "#7c3aed", bg: "bg-violet-50",  border: "border-violet-600",  text: "text-violet-700",  tag: "CR NOTE" },
    debit_note:       { color: "#ea580c", bg: "bg-orange-50",  border: "border-orange-600",  text: "text-orange-700",  tag: "DR NOTE" },
  };
  const s = DOC_STYLES[d.type] || { color: "#a67c00", bg: "bg-amber-50", border: "border-amber-600", text: "text-amber-700", tag: "DOCUMENT" };

  return (
    <PrintFrame title={`${d.doc_type_label} ${d.doc_no}`}>
      <div className={`border-b-2 ${s.border} pb-4 mb-6 flex items-start justify-between`}>
        <div>
          <div className={`text-2xl font-bold uppercase ${s.text}`}>{d.doc_type_label}</div>
          <div className="mt-1">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-widest ${s.bg} ${s.text} border ${s.border}`} data-testid={`doc-tag-${d.type}`}>
              {s.tag}
            </span>
            <span className="ml-2 text-xs text-slate-500">Not a Tax Invoice</span>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-lg font-bold ${s.text}`}>{d.type !== "delivery_challan" && d.distributor?.name ? d.distributor.name : d.company_name}</div>
          {d.type !== "delivery_challan" && d.distributor && (<>
            {d.distributor.address && <div className="text-xs text-slate-600">{d.distributor.address}</div>}
            {d.distributor.phone && <div className="text-xs text-slate-600">Ph: {d.distributor.phone}</div>}
            {(d.distributor.gstin || d.distributor.gst_number) && <div className="text-xs text-slate-600">GSTIN: {d.distributor.gstin || d.distributor.gst_number}</div>}
          </>)}
          <div className="text-xs text-slate-500 mt-1">{d.doc_type_label} No: <span className="font-mono">{d.doc_no}</span></div>
          <div className="text-xs text-slate-500">Date: {d.date}</div>
        </div>
      </div>
      <div className={`grid grid-cols-2 gap-8 mb-6 p-3 rounded-lg ${s.bg} border ${s.border}`}>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Party</div>
          <div className="font-semibold text-slate-900">{d.party_name}</div>
          {d.party?.address && <div className="text-xs text-slate-600">{d.party.address}</div>}
          {d.party?.phone && <div className="text-xs text-slate-600">Ph: {d.party.phone}</div>}
          {d.party?.gstin && <div className="text-xs text-slate-600">GSTIN: {d.party.gstin}</div>}
        </div>
        <div>
          <Row label="Doc No" value={d.doc_no} />
          <Row label="Date" value={d.date} />
          {d.ref_invoice_no && <Row label="Against Invoice" value={d.ref_invoice_no} />}
          {d.ref_invoice_date && <Row label="Invoice Date" value={d.ref_invoice_date} />}
        </div>
      </div>
      {d.type !== "delivery_challan" ? (
      <table className="w-full text-sm border border-slate-200 mb-6">
        <thead><tr className={`${s.bg} text-left`}>
          <th className="p-2">Product</th>
          <th className="p-2 text-right">Quantity</th>
          <th className="p-2 text-right">Unit</th>
          <th className="p-2 text-right">No. of Pieces</th>
          <th className="p-2 text-right">No. of Liters</th>
          <th className="p-2 text-right">Rate</th>
          <th className="p-2 text-right">GST</th>
          <th className="p-2 text-right">Amount</th>
        </tr></thead>
        <tbody>{(d.items || []).map((it, i) => (
          <tr key={i} className="border-t border-slate-100">
            <td className="p-2">{it.description}</td>
            <td className="p-2 text-right">{it.qty}</td>
            <td className="p-2 text-right">{it.no_of_pieces != null ? "Pcs" : "-"}</td>
            <td className="p-2 text-right">{it.no_of_pieces != null ? it.no_of_pieces : "—"}</td>
            <td className="p-2 text-right">{it.no_of_liters ? Number(it.no_of_liters).toFixed(2) : "—"}</td>
            <td className="p-2 text-right">{inr(it.rate)}</td>
            <td className="p-2 text-right">{inr(it.line_gst)}</td>
            <td className="p-2 text-right font-semibold">{inr((it.amount || 0) + (it.line_gst || 0))}</td>
          </tr>
        ))}</tbody>
        <tfoot>
          <tr className={`border-t-2 border-slate-800 ${s.bg} font-semibold text-slate-900`}>
            <td className="p-2 text-right">Total</td>
            <td className="p-2 text-right">{Math.round((d.items || []).reduce((a, it) => a + (Number(it.qty) || 0), 0) * 100) / 100}</td>
            <td className="p-2"></td>
            <td className="p-2 text-right">{(d.items || []).reduce((a, it) => a + (it.no_of_pieces || 0), 0) || "—"}</td>
            <td className="p-2 text-right">{(() => { const l = (d.items || []).reduce((a, it) => a + (it.no_of_liters || 0), 0); return l ? l.toFixed(2) : "—"; })()}</td>
            <td className="p-2"></td>
            <td className="p-2"></td>
            <td className="p-2"></td>
          </tr>
        </tfoot>
      </table>
      ) : (
      <table className="w-full text-sm border border-slate-200 mb-6">
        <thead><tr className={`${s.bg} text-left`}><th className="p-2">#</th><th className="p-2">Description</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Rate</th><th className="p-2 text-right">Amount</th></tr></thead>
        <tbody>{(d.items || []).map((it, i) => (
          <tr key={i} className="border-t border-slate-100">
            <td className="p-2">{i + 1}</td><td className="p-2">{it.description}</td>
            <td className="p-2 text-right">{it.qty}</td>
            <td className="p-2 text-right">{inr(it.rate)}</td>
            <td className="p-2 text-right font-semibold">{inr(it.amount)}</td>
          </tr>
        ))}</tbody>
      </table>
      )}
      <div className="flex justify-end">
        <div className="w-64">
          <Row label="Subtotal" value={inr(d.subtotal)} />
          <Row label={`GST (${d.gst_pct}%)`} value={inr(d.gst_total)} />
          <div className="border-t border-slate-200 mt-2 pt-2"><Row label="Grand Total" value={<span className={`text-lg font-bold ${s.text}`}>{inr(d.total)}</span>} /></div>
        </div>
      </div>
      {d.notes && <div className="mt-6 p-3 rounded-lg bg-slate-50 text-sm text-slate-700"><strong>Notes:</strong> {d.notes}</div>}
      {d.invoice_message && <div className={`mt-4 p-3 rounded-lg ${s.bg} border ${s.border} text-sm text-slate-700`}>{d.invoice_message}</div>}
      {d.invoice_terms && <div className="mt-4 border-t border-slate-200 pt-3"><div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Terms &amp; Conditions</div><div className="text-xs text-slate-600 whitespace-pre-wrap">{d.invoice_terms}</div></div>}
    </PrintFrame>
  );
}
